import React, { useEffect } from 'react'
import tw, { styled } from 'twin.macro'
import Board from './Board'
import Welcome from './Welcome'
import EndScreen from './EndScreen'
import { useStateContext } from './../context/stateContext'
import { AnimatePresence } from 'framer-motion'
import logo from './../assets/sk-logo-black.png'
import logoWhite from './../assets/sk-logo.png'
import Popup from './Popup'
import eggYellow from './../assets/egg-yellow.svg'
import eggBlue from './../assets/egg-blue.svg'

const Container = styled.div`
	${tw`relative flex items-center justify-center w-full h-screen overflow-hidden`}
`
const DesignBy = styled.p`
	${tw`absolute text-right text-black text-10px md:text-sm bottom-2 right-2 md:bottom-8 md:right-8`}/* ${({ colorMode }) => (colorMode === 'yellow' ? tw`text-black` : tw`text-white`)} */
`

const BackgroundContainer = styled.div`
	${tw`inset-0 object-cover w-full h-screen z-[-10] relative`}
`
const EggsContainer = styled.div`
	${tw`absolute inset-0 object-cover w-full h-screen z-[-5]`}
	pointer-events: none;
`
const Egg = styled.img`
	position: absolute;
	transition: left 0.3s, top 0.3s, transform 0.3s;
	pointer-events: auto;
`

const Screen = () => {
	const stateContext = useStateContext()
	const { colorMode, gameStatus } = stateContext.state
	const { loadSounds } = useStateContext()
	const gameContainerRef = React.useRef()
	const [bgColor, setBgColor] = React.useState()
	const [eggs, setEggs] = React.useState([])

	const checkCollision = (newEgg, eggs) => {
		for (let egg of eggs) {
			if (Math.abs(newEgg.left - egg.left) < 150 && Math.abs(newEgg.top - egg.top) < 150) {
				return true
			}
		}
		return false
	}

	const isSimilarInArea = (newEgg, eggs, area) => {
		return eggs.some((egg) => {
			const distance = Math.sqrt(Math.pow(newEgg.left - egg.left, 2) + Math.pow(newEgg.top - egg.top, 2))
			return distance < area && Math.abs(newEgg.rotation - egg.rotation) < 20 && Math.abs(newEgg.scale - egg.scale) < 0.1
		})
	}

	useEffect(() => {
		const newEggs = []
		for (let i = 0; i < 100; i++) {
			let attempts = 0
			let newEgg
			do {
				const rotation = Math.random() * 360
				const windowScale = Math.max(0.9, Math.min(window.innerWidth / 1920, window.innerHeight / 1000))
				const scale = (0.7 + Math.random() * 0.3) * windowScale
				const left = Math.random() * window.innerWidth
				const top = Math.random() * window.innerHeight
				newEgg = { rotation, scale, left, top, originalLeft: left, originalTop: top }
				attempts++
			} while (
				(checkCollision(newEgg, newEggs) ||
					isSimilarInArea(newEgg, newEggs, 400) ||
					(window.innerWidth > 640 && newEgg.left > window.innerWidth * 0.2 && newEgg.left < window.innerWidth * 0.8 && newEgg.top > window.innerHeight * 0.2 && newEgg.top < window.innerHeight * 0.7) ||
					(window.innerWidth > 640 && newEgg.left > window.innerWidth * 0.4 && newEgg.left < window.innerWidth * 0.6 && newEgg.top < window.innerHeight * 0.2 + 50) ||
					(window.innerWidth > 640 && newEgg.left > window.innerWidth * 0.7 && newEgg.left < window.innerWidth * 1 && newEgg.top > window.innerHeight * 0.8 && newEgg.top < window.innerHeight * 1) ||
					(window.innerWidth < 640 && newEgg.top > window.innerHeight * 0.2 && newEgg.top < window.innerHeight * 0.7) ||
					(window.innerWidth < 640 && newEgg.left > window.innerWidth * 0.25 && newEgg.left < window.innerWidth * 0.75 && newEgg.top < window.innerHeight * 0.1 + 50)) &&
				attempts < 100
			)
			if (attempts < 100) {
				const randomDuration = 2 + Math.random() * 2 // random value between 2 and 4
				newEgg = { ...newEgg, duration: randomDuration }
				newEggs.push(newEgg)
			}
		}
		setEggs(newEggs)
	}, [])

	useEffect(() => {
		for (let i = 0; i < eggs.length; i++) {
			const style = document.createElement('style')
			const randomTranslateX = Math.random() * 20 - 10 // random value between -10 and 10
			const randomTranslateY = Math.random() * 20 - 10 // random value between -10 and 10
			const randomRotate = Math.random() * 40 - 20 // random value between -10 and 10
			const randomScale = 1 + Math.random() * 0.2 - 0.1 // random value between 0.9 and 1.1
			const randomDuration = 2 + Math.random() * 2 // random value between 2 and 4
			style.innerHTML = `
				@keyframes float${i} {
					0% {
						transform: translate(0px, 0px) rotate(${eggs[i].rotation}deg) scale(${gameStatus === 'gameStart' ? 0 : eggs[i].scale});
					}
					50% {
						transform: translate(${randomTranslateX}px, ${randomTranslateY}px) rotate(${eggs[i].rotation + randomRotate}deg) scale(${gameStatus === 'gameStart' ? 0 : eggs[i].scale * randomScale});
					}
					100% {
						transform: translate(0px, 0px) rotate(${eggs[i].rotation}deg) scale(${gameStatus === 'gameStart' ? 0 : eggs[i].scale});
					}
				}
			`
			document.head.appendChild(style)
		}
	}, [eggs, gameStatus])

	const colorMap = {
		green: 'bg-green',
		pink: 'bg-pink',
		yellow: 'bg-yellow',
	}

	useEffect(() => {
		if (!stateContext.state.soundLoaded) {
			loadSounds()
		}
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		setBgColor(colorMap[colorMode])
	}, [stateContext])

	return (
		<Container ref={gameContainerRef}>
			<BackgroundContainer className={bgColor}></BackgroundContainer>

			<AnimatePresence exitBeforeEnter initial={false}>
				<img className='absolute left-0 right-0 block h-[60px] sm:h-auto object-contain mx-auto mb-4 top-5 sm:top-10 md:top-14 ' src={stateContext.state.gameStatus === 'gameStart' && (colorMode === 'pink' || colorMode === 'green') ? logoWhite : logo} alt='' />
				{stateContext.state.gameStatus === 'beforeInit' && <Welcome key='welcome' />}
				{stateContext.state.gameStatus === 'gameStart' && <Board key='board' />}
				{stateContext.state.gameStatus === 'gameEnded' && <EndScreen key='endscreen' />}
				<EggsContainer key='eggscontainer'>
					{eggs.map((egg, index) => (
						<Egg
							key={index}
							src={colorMode == 'yellow' ? eggBlue : eggYellow}
							alt=''
							style={{
								left: `${egg.left}px`,
								top: `${egg.top}px`,
								transform: `rotate(${egg.rotation}deg) scale(${gameStatus === 'gameStart' ? 0 : egg.scale})`,
								animation: `float${index} ${egg.duration}s ease-in-out infinite`,
							}}
						/>
					))}
				</EggsContainer>
			</AnimatePresence>
			<DesignBy colorMode={colorMode}>
				designed & developed by <span className='block md:inline-block'>Hattenberger&nbsp;Partner</span>
			</DesignBy>
			{stateContext.state.popup && <Popup key='popup' />}
		</Container>
	)
}

export default Screen
